import { Box, Typography } from "@mui/material";
import { ReactComponent as YoutubeIcon } from "../../static/svg/youtube.svg";

type Platform = {
  label: string;
  name: string;
  url: string;
  icon: React.ReactNode;
};

type Props = {
  spotifyUrl?: string;
  appleUrl?: string;
  googleUrl?: string;
};

const SpotifyIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 28, height: 28, flexShrink: 0 }}
    fill="#1DB954"
  >
    <circle cx="12" cy="12" r="12" fill="black" />
    <path
      fill="#fff"
      d="M17.9 10.9C14.7 9 9.35 8.8 6.3 9.75c-.5.15-1-.15-1.15-.6-.15-.5.15-1 .6-1.15 3.55-1.05 9.4-.85 13.1 1.35.45.25.6.85.35 1.3-.25.35-.85.5-1.3.25zm-.1 2.8c-.25.35-.7.5-1.05.25-2.7-1.65-6.8-2.15-9.95-1.15-.4.1-.8-.1-.9-.5-.1-.4.1-.8.5-.9 3.65-1.1 8.15-.55 11.25 1.35.3.15.45.65.15 1zm-1.2 2.75c-.2.3-.55.4-.85.2-2.35-1.45-5.3-1.75-8.8-.95-.35.1-.65-.15-.75-.45-.1-.35.15-.65.45-.75 3.8-.85 7.1-.5 9.7 1.1.35.15.4.55.25.85z"
    />
  </Box>
);

const AppleIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 28, height: 28, flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="12" fill="#9933CC" />
    <path
      fill="#fff"
      d="M12 4.5c-1.8 0-3.3 1.1-3.9 2.6-.3.7-.4 1.5-.3 2.2-1.2.6-2 1.9-2 3.3 0 2 1.6 3.7 3.7 3.9v.5c0 .6-.1 1.2-.3 1.7-.1.3 0 .6.3.7.1 0 .2.1.3.1.2 0 .4-.1.5-.3.3-.7.4-1.4.4-2.2v-.5c.1 0 .2 0 .3.1.1 0 .2 0 .3-.1v.5c0 .8.1 1.5.4 2.2.1.2.3.3.5.3.1 0 .2 0 .3-.1.3-.1.4-.4.3-.7-.2-.5-.3-1.1-.3-1.7v-.5c2.1-.2 3.7-1.9 3.7-3.9 0-1.5-.8-2.7-2-3.3.1-.8 0-1.5-.3-2.2-.6-1.5-2.1-2.6-3.9-2.6zm0 1.2c1.3 0 2.4.8 2.9 1.9.2.5.3 1 .2 1.5-.3-.1-.7-.1-1-.1h-4.2c-.4 0-.7 0-1 .1-.1-.5 0-1 .2-1.5.5-1.1 1.6-1.9 2.9-1.9zm0 4.5c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3zm0 1.5c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5 1.5-.7 1.5-1.5-.7-1.5-1.5-1.5z"
    />
  </Box>
);

// const GoogleIcon = () => (
//   <Box
//     component="svg"
//     viewBox="0 0 24 24"
//     sx={{ width: 28, height: 28, flexShrink: 0 }}
//   >
//     <rect width="5" height="14" x="1" y="5" rx="2.5" fill="#0066D9" />
//     <rect width="5" height="10" x="7" y="1" rx="2.5" fill="#EA4335" />
//     <rect width="5" height="18" x="13" y="3" rx="2.5" fill="#34A853" />
//     <rect width="5" height="8" x="19" y="8" rx="2.5" fill="#FBBC04" />
//   </Box>
// );

const PodcastPlatformLinks = ({
  spotifyUrl = "#",
  appleUrl = "#",
  googleUrl = "#",
}: Props) => {
  const platforms: Platform[] = [
    {
      label: "Listen on",
      name: "Spotify",
      url: spotifyUrl,
      icon: <SpotifyIcon />,
    },
    {
      label: "Listen on",
      name: "Apple Podcasts",
      url: appleUrl,
      icon: <AppleIcon />,
    },
    {
      label: "Watch on",
      name: "Youtube Podcasts",
      url: googleUrl,
      icon: <YoutubeIcon />,
    },
    // {
    //   label: "Listen on",
    //   name: "Google Podcasts",
    //   url: googleUrl,
    //   icon: <GoogleIcon />,
    // },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: { xs: 1.5, sm: 2 },
        justifyContent: { xs: "center", sm: "flex-start" },
        width: "100%",
      }}
    >
      {platforms.map((p) => (
        <Box
          key={p.name}
          component="a"
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: { xs: 2, sm: 2.5 },
            py: 1.25,
            borderRadius: "12px",
            border: "1.5px solid rgba(255,255,255,0.5)",
            backgroundColor: "#fff",
            textDecoration: "none",
            transition: "background-color 0.2s, border-color 0.2s",
            "&:hover": {
              borderColor: "#194049",
              textDecoration: "none",
            },
          }}
        >
          {p.icon}
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <Typography
              sx={{
                fontSize: "10px",
                color: "#194049",
                letterSpacing: "0.02em",
                lineHeight: 1.4,
              }}
            >
              {p.label}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "15px" },
                fontWeight: 600,
                color: "#194049",
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              {p.name}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default PodcastPlatformLinks;
